import React, { useCallback, useState } from 'react';

import {
  List,
  useEditContext,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useUpdate,
} from 'react-admin';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  Divider,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import { Add, AddCircle } from '@mui/icons-material';

import useDialogStatus from '../../../hooks/useDialogStatus';
import errorNotification from '../../../utilities/errorNotification';

import Pagination from '../../layout/Pagination';

import { AccountEntityListBody, AccountEntityFilters } from '../../accountEntity/AccountEntityList';
import { getIndividualFilters, IndividualListBody } from '../../individual/IndividualList';
import { getBusinessFilters, BusinessListBody } from '../../business/BusinessList';
import { CarFilters, CarListBody } from '../../car/CarList';
import { ProductFilters, ProductListBody } from '../../product/ProductList';

import hasAccess from '../../../utilities/hasAccess';

import entityTypes, { EntityTypes, KnownEntityTypes, resourceFromEntityTypes } from '../../../constants/entityTypes';
import actions, { Actions } from '../../../constants/actions';
import FilterActions from '../../filters/FilterActions';

const LIST_BODY = {
  [entityTypes.INDIVIDUAL]: IndividualListBody,
  [entityTypes.BUSINESS]: BusinessListBody,
  [entityTypes.CAR]: CarListBody,
  [entityTypes.ACCOUNT]: AccountEntityListBody,
  [entityTypes.PRODUCT]: ProductListBody,
} as const;

const LIST_FILTERS = {
  [entityTypes.INDIVIDUAL]: getIndividualFilters({}),
  [entityTypes.BUSINESS]: getBusinessFilters({}),
  [entityTypes.CAR]: CarFilters,
  [entityTypes.ACCOUNT]: AccountEntityFilters,
  [entityTypes.PRODUCT]: ProductFilters,
} as const;

const AddButton = ({
  closeDialog,
  entityType,
  attachedEntities,
}: {
  closeDialog: () => void;
  entityType: EntityTypes;
  attachedEntities: {id: string; type: EntityTypes}[];
}) => {
  const { record: { id } } = useEditContext();
  const record = useRecordContext();
  const [update] = useUpdate();
  const refetch = useRefresh();
  const notify = useNotify();

  const handleClick = useCallback(() => {
    update('cases', {
      id: `${id}/attach-entity`,
      data: {
        entityId: record?.id,
        entityType,
      },
    }, {
      mutationMode: 'pessimistic',
      onSuccess: () => {
        closeDialog();
        refetch();
      },
      onError: (error) => {
        notify(errorNotification(error), { type: 'error' });
        closeDialog();
      },
    });
  }, [closeDialog, entityType, id, notify, record?.id, refetch, update]);

  return (
    <Button
      startIcon={<AddCircle />}
      onClick={handleClick}
      disabled={attachedEntities.some((entity) => entity.id === record?.id)}
    >
      Add
    </Button>
  );
};

const entityAndPermissionMap: Record<KnownEntityTypes, Actions> = {
  [entityTypes.ACCOUNT]: actions.ACCOUNT_ENTITY_LIST,
  [entityTypes.BUSINESS]: actions.BUSINESS_LIST,
  [entityTypes.CAR]: actions.CAR_LIST,
  [entityTypes.INDIVIDUAL]: actions.INDIVIDUAL_LIST,
  [entityTypes.PRODUCT]: actions.PRODUCT_LIST,
};

const EntityTabs = ({
  value,
  onChange,
}: {
  value: KnownEntityTypes;
  onChange: (e: React.SyntheticEvent, newValue: KnownEntityTypes) => void;
}) => {
  const { permissions } = usePermissions<Actions[]>();
  return (
    <Tabs value={value} onChange={onChange}>
      {Object.values(entityTypes)
        .filter((entityType): entityType is KnownEntityTypes => entityType !== entityTypes.UNKNOWN)
        .map((entityType) => (
          hasAccess(permissions, entityAndPermissionMap[entityType])
        && <Tab label={entityType} value={entityType} key={entityType} />
        ))}
    </Tabs>
  );
};

const AddEntities = () => {
  const record = useRecordContext();
  const [value, setValue] = useState<KnownEntityTypes>(entityTypes.INDIVIDUAL);
  const { open, openDialog, closeDialog } = useDialogStatus();

  const ListBody = LIST_BODY[value];
  const resource = resourceFromEntityTypes[value];
  const filters = LIST_FILTERS[value];

  const onChange = useCallback((e: React.SyntheticEvent, newValue: KnownEntityTypes) => {
    setValue(newValue);
  }, []);

  const { permissions } = usePermissions();
  if (!hasAccess(permissions, actions.CASE_MANAGE_ENTITIES)) return null;

  return (
    <>
      <Button
        startIcon={<Add />}
        onClick={openDialog}
      >
        Attach entities
      </Button>
      <Dialog scroll="body" transitionDuration={500} open={open} fullWidth maxWidth="xl">
        <Card style={{ margin: 0 }}>
          <CardHeader
            title="Attach entities"
            action={(
              <IconButton onClick={closeDialog}>
                <CloseIcon />
              </IconButton>
            )}
          />
          <Divider />
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
              <EntityTabs value={value} onChange={onChange} />
            </Box>
            <List
              disableSyncWithLocation
              actions={<FilterActions />}
              resource={resource}
              filters={filters}
              empty={false}
              pagination={<Pagination disableEmptyText />}
            >
              <Card sx={{ margin: 0 }} variant="outlined">
                <ListBody>
                  <AddButton
                    entityType={value}
                    closeDialog={closeDialog}
                    attachedEntities={record?.entities}
                  />
                </ListBody>
              </Card>
            </List>
          </CardContent>
        </Card>
      </Dialog>
    </>
  );
};

export default AddEntities;
